import { getTaps } from './runtime';
import type { ScopeId, StoreKey } from './types';

type Kind = StoreKey['map'];
type InstIdOf = (id: string) => string | undefined;

/**
 * Threads the pulse-tap args that stay constant for one whole store — the tap module,
 * the store's `scopeId`, and its `getState` — so each map setter only passes what varies:
 * the kind, the state field, the raw setter, and an optional instance-id extractor.
 *
 * When pulse is off (`getTaps()` is undefined), every helper hands back the raw setter untouched —
 * no wrapper frame, byte-for-byte the old `taps ? taps.tapX(...) : raw` behaviour, so the runner
 * pays nothing.
 *
 * `capture` is the per-store gate: `getTaps()` is a process-global, so once the builder wires it,
 * every store created afterward would tap — including RUNNER-mode interface trees rendered inside
 * the same builder process (platform manager pages via PlatformInterfaceProvider). Those pass
 * capture=false so their setters stay raw and never leak into Pulse, even with taps globally wired.
 *
 * Each store taps only the ops it needs: call the helper for the ops you want captured and
 * leave the rest as their raw setters (e.g. the app store deliberately never taps its
 * pageVariables multi setter — it has no live caller, so wrapping it would tap a dead path).
 */
export function createTapBinder(scopeId: ScopeId, getState: () => object, capture: boolean) {
  const taps = getTaps();
  const active = taps && capture ? { taps, scopeId } : undefined;

  return {
    mapSetter: <V>(
      map: Kind,
      field: string,
      raw: (id: string, value: V) => void,
      instIdOf?: InstIdOf,
    ) =>
      active ? active.taps.tapMapSetter(active.scopeId, map, getState, field, raw, instIdOf) : raw,

    multiSetter: <V>(
      map: Kind,
      field: string,
      raw: (values: Partial<Record<string, V>>) => void,
      instIdOf?: InstIdOf,
    ) =>
      active
        ? active.taps.tapMultiSetter(active.scopeId, map, getState, field, raw, instIdOf)
        : raw,

    updater: <F extends (id: string, mutator: never) => void>(
      map: Kind,
      field: string,
      raw: F,
      instIdOf?: InstIdOf,
    ): F =>
      active ? active.taps.tapUpdater(active.scopeId, map, getState, field, raw, instIdOf) : raw,

    mapRemover: (map: Kind, field: string, raw: (id: string) => void, instIdOf?: InstIdOf) =>
      active ? active.taps.tapMapRemover(active.scopeId, map, getState, field, raw, instIdOf) : raw,

    mapMultiRemover: (
      map: Kind,
      field: string,
      raw: (ids: string[]) => void,
      instIdOf?: InstIdOf,
    ) =>
      active
        ? active.taps.tapMapMultiRemover(active.scopeId, map, getState, field, raw, instIdOf)
        : raw,
  };
}
